/**
 * SASADM-0155 — the brand editor's Translations tab (saas-auth SASATH-0346,
 * handoff "go" §3.5 and #962 §4.4).
 *
 * - A keys × locales grid from `GET …/copy-locales`. Each cell's placeholder is
 *   the default-locale string, which is also shown in a read-only column; the
 *   defaults themselves are edited on the legacy brand form.
 * - Edits are tracked as a diff against the loaded values. Save translations
 *   sends only the changed cells: trimmed text, or `null` for a cleared cell.
 * - A 200 replaces the grid from the response and calls `onSaved` — the save
 *   changed the theme etag ("go" §4.2), so the page marks the Theme tab stale.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { DEFAULT_RETRY_AFTER_SECONDS } from '@/api/client';
import { ApiError } from '@/api/errors';
import type { CopyLocalesResponse, CopyLocalesUpdate } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';

/** The server's per-translation limit ("go" §3.5). */
export const TRANSLATION_MAX_LENGTH = 2000;

/** locale → key → the cell's text as typed (only cells the user touched). */
type Edits = Record<string, Record<string, string>>;

/** The PUT body's `locales`: only cells whose trimmed text differs from the loaded value. */
function changedCells(data: CopyLocalesResponse, edits: Edits): CopyLocalesUpdate['locales'] {
  const out: CopyLocalesUpdate['locales'] = {};
  for (const [locale, cells] of Object.entries(edits)) {
    for (const [key, raw] of Object.entries(cells)) {
      const next = raw.trim();
      if (next === (data.locales[locale]?.[key] ?? '')) continue;
      (out[locale] ??= {})[key] = next === '' ? null : next;
    }
  }
  return out;
}

interface BrandTranslationsPanelProps {
  brandId: number;
  /** Bumped by the page after a legacy save — the defaults may have changed. */
  staleToken: number;
  /** Whether the Translations tab is showing. */
  active: boolean;
  /** Called after a 200 save (the theme etag changed). */
  onSaved: () => void;
}

export function BrandTranslationsPanel({
  brandId,
  staleToken,
  active,
  onSaved,
}: BrandTranslationsPanelProps) {
  const { t } = useCopy();
  const [data, setData] = useState<CopyLocalesResponse | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // The staleToken the last GET covered; -1 until the first load.
  const loadedToken = useRef(-1);

  /** GET copy-locales. Unsaved edits are kept: they are a diff against the new values. */
  async function load() {
    const token = staleToken;
    try {
      const res = await apiAdmin.brandCopyLocales.get(brandId);
      loadedToken.current = token;
      setLoadError(null);
      setData(res);
    } catch (err) {
      if (err instanceof ApiError) setLoadError(err);
    }
  }

  // Load on first show, and re-GET when the tab becomes visible after a legacy
  // save (the default strings, and so the placeholders, may have changed).
  useEffect(() => {
    if (!active || loadedToken.current === staleToken) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on visibility/staleness only
  }, [active, staleToken]);

  function setCell(locale: string, key: string, value: string) {
    setEdits((prev) => ({ ...prev, [locale]: { ...prev[locale], [key]: value } }));
    setSaveError(null);
    setSaved(false);
  }

  if (loadError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void load()}>
            {t('brands.translations.reload', 'Reload')}
          </Button>
        }
      >
        {t('brands.translations.loadFailed', "Couldn't load the translations.")}
      </Alert>
    );
  }
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={t('brands.translations.loading', 'Loading translations')} />
      </Box>
    );
  }

  const locales = Object.keys(data.locales);
  const changes = changedCells(data, edits);
  const hasChanges = Object.keys(changes).length > 0;
  const tooLong = Object.values(edits).some((cells) =>
    Object.values(cells).some((v) => v.trim().length > TRANSLATION_MAX_LENGTH),
  );

  async function handleSave() {
    if (!data) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const res = await apiAdmin.brandCopyLocales.save(brandId, changedCells(data, edits));
      setData(res);
      setEdits({});
      setSaved(true);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  function saveErrorMessage(err: ApiError): string {
    if (err.status === 429) {
      return t('brands.translations.rateLimited', 'Too many saves — try again in {{n}} s', {
        n: err.details.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
      });
    }
    return (
      err.details.message ??
      t(`errors.codes.${err.code}`, t('errors.generic', 'Something went wrong.'))
    );
  }

  const keyLabel = (key: string) => t(`brands.translations.keys.${key}`, key);
  const localeLabel = (locale: string) => t(`brands.translations.locales.${locale}`, locale);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h3" gutterBottom>
            {t('brands.translations.title', 'Translations')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'brands.translations.hint',
              'Blank cells use the default text. Default text is edited in the Advanced tab.',
            )}
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('brands.translations.title', 'Translations')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('brands.translations.key', 'Text')}</TableCell>
                <TableCell>
                  {t('brands.translations.defaultColumn', 'Default ({{locale}})', {
                    locale: data.defaultLocale,
                  })}
                </TableCell>
                {locales.map((locale) => (
                  <TableCell key={locale} sx={{ minWidth: 220 }}>
                    <Typography variant="subtitle2" component="span" sx={{ display: 'block' }}>
                      {localeLabel(locale)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      data-testid={`missing-${locale}`}
                    >
                      {t('brands.translations.missing', '{{n}} missing', {
                        n: data.missing[locale]?.length ?? 0,
                      })}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.keys.map((key) => {
                const def = data.default[key] ?? '';
                return (
                  <TableRow key={key} data-testid={`translation-row-${key}`}>
                    <TableCell>{keyLabel(key)}</TableCell>
                    <TableCell sx={{ color: def ? 'text.primary' : 'text.secondary' }}>
                      {def || t('brands.translations.noDefault', 'Not set')}
                    </TableCell>
                    {locales.map((locale) => {
                      const value = edits[locale]?.[key] ?? data.locales[locale]?.[key] ?? '';
                      const over = value.trim().length > TRANSLATION_MAX_LENGTH;
                      return (
                        <TableCell key={locale}>
                          <TextField
                            fullWidth
                            size="small"
                            multiline={key === 'heroCopy'}
                            // Locked while saving: a 200 clears the edits, so text typed
                            // after the body was built would otherwise be lost.
                            disabled={saving}
                            value={value}
                            placeholder={def}
                            onChange={(e) => setCell(locale, key, e.target.value)}
                            error={over}
                            helperText={
                              over
                                ? t('brands.translations.tooLong', 'At most {{n}} characters.', {
                                    n: TRANSLATION_MAX_LENGTH,
                                  })
                                : ''
                            }
                            slotProps={{
                              htmlInput: {
                                maxLength: TRANSLATION_MAX_LENGTH,
                                'aria-label': t(
                                  'brands.translations.cellLabel',
                                  '{{key}} ({{locale}})',
                                  { key: keyLabel(key), locale },
                                ),
                              },
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {saveError ? (
          <Alert severity="error" data-testid="translations-save-error">
            {saveErrorMessage(saveError)}
          </Alert>
        ) : null}
        {saved ? (
          <Alert severity="success">{t('brands.translations.saved', 'Translations saved.')}</Alert>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={!hasChanges || tooLong || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {t('brands.translations.save', 'Save translations')}
          </Button>
          {hasChanges ? (
            <Typography variant="caption" color="text.secondary">
              {t('brands.translations.unsaved', 'Unsaved translation changes')}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
